import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from './api';
import { Loader2, Upload, RotateCcw, ImageIcon, Search, Check } from 'lucide-react';
import { getSlots } from '../data/assetSlots';
import { useAssets } from '../assets/AssetsContext';

/**
 * Site-asset manager.
 * Lists every replaceable image slot on the site grouped by section. The admin
 * can upload a replacement (binds slot -> uploaded media id) or reset to default.
 */
export default function MediaPanel() {
  const slots = useMemo(() => getSlots(), []);
  const { overrides, refresh, apiBase } = useAssets();
  const [filter, setFilter] = useState('');
  const [busyKey, setBusyKey] = useState(null);
  const [okKey, setOkKey] = useState(null);
  const fileRefs = useRef({});

  // Group slots by `group`
  const groups = useMemo(() => {
    const filt = filter.trim().toLowerCase();
    const map = new Map();
    for (const s of slots) {
      if (filt && !`${s.group} ${s.label} ${s.key}`.toLowerCase().includes(filt)) continue;
      if (!map.has(s.group)) map.set(s.group, []);
      map.get(s.group).push(s);
    }
    return [...map.entries()];
  }, [slots, filter]);

  const counts = useMemo(() => ({
    total: slots.length,
    overridden: Object.keys(overrides).filter((k) => slots.find((s) => s.key === k)).length,
  }), [slots, overrides]);

  const onUpload = async (slot, file) => {
    if (!file) return;
    const isVideo = (file.type || '').startsWith('video/');
    const max = isVideo ? 50 * 1024 * 1024 : 6 * 1024 * 1024;
    if (file.size > max) {
      alert(isVideo ? 'Video exceeds 50 MB' : 'Image exceeds 6 MB');
      return;
    }
    if (slot.kind === 'image' && isVideo) {
      alert('This slot only accepts images.');
      return;
    }
    setBusyKey(slot.key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.put(`/api/admin/assets/${encodeURIComponent(slot.key)}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refresh();
      setOkKey(slot.key); setTimeout(() => setOkKey(null), 1500);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusyKey(null);
    }
  };

  const onReset = async (slot) => {
    if (!window.confirm('Reset this image to the original?')) return;
    setBusyKey(slot.key);
    try {
      await api.delete(`/api/admin/assets/${encodeURIComponent(slot.key)}`);
      await refresh();
    } finally { setBusyKey(null); }
  };

  return (
    <div className="space-y-6" data-testid="media-panel">
      {/* Header */}
      <div className="bg-[#124D3A] border border-[#D4AF37]/15 rounded-3xl p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div>
          <h3 className="font-serif-display text-xl text-white">Site media</h3>
          <p className="text-sm text-white/60 mt-1">
            {counts.total} replaceable slots · <span className="text-[#D4AF37]">{counts.overridden}</span> customized · images up to 6 MB · videos up to 50 MB (MP4 / WEBM / MOV)
          </p>
        </div>
        <div className="relative w-full md:w-80">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]" />
          <input
            placeholder="Search section, label or key…"
            value={filter} onChange={(e) => setFilter(e.target.value)}
            data-testid="media-search"
            className="w-full pl-11 pr-4 py-2.5 rounded-full bg-[#072A1F] border border-[#D4AF37]/25 text-white placeholder:text-white/40 outline-none focus:border-[#D4AF37]"
          />
        </div>
      </div>

      {/* Groups */}
      {groups.length === 0 && (
        <div className="bg-[#124D3A] border border-[#D4AF37]/15 rounded-3xl p-12 text-center text-white/55">
          <ImageIcon className="w-10 h-10 mx-auto mb-3 opacity-50" />
          No images match your search.
        </div>
      )}
      {groups.map(([group, items]) => (
        <details key={group} open className="group bg-[#124D3A] border border-[#D4AF37]/15 rounded-3xl overflow-hidden">
          <summary className="cursor-pointer list-none px-6 py-4 flex items-center justify-between bg-[#083325] hover:bg-[#072A1F] transition-colors">
            <span className="font-serif-display text-lg text-white">{group}</span>
            <span className="text-xs text-white/55">{items.length} image{items.length !== 1 ? 's' : ''}</span>
          </summary>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-5">
            {items.map((slot) => {
              const ov = overrides[slot.key];
              const url = ov ? `${apiBase}${ov.url}` : slot.defaultUrl;
              const isBusy = busyKey === slot.key;
              const isOk = okKey === slot.key;
              const overridden = !!ov;
              const slotIsVideo = slot.kind === 'video';
              const overrideIsVideo = ov?.kind === 'video' || (ov?.mime || '').startsWith('video/');
              const showVideo = overrideIsVideo || (slotIsVideo && !overridden);
              const accept = slotIsVideo
                ? 'video/mp4,video/webm,video/quicktime,video/ogg'
                : 'image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime,video/ogg';
              return (
                <div
                  key={slot.key}
                  data-testid={`asset-card-${slot.key}`}
                  className={`relative bg-[#083325] border rounded-2xl overflow-hidden ${
                    overridden ? 'border-[#D4AF37]/60' : 'border-[#D4AF37]/15'
                  }`}
                >
                  {overridden && (
                    <div className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] bg-[#D4AF37] text-[#0B3D2E] font-bold">
                      {overrideIsVideo ? 'video' : 'custom'}
                    </div>
                  )}
                  {slotIsVideo && !overridden && (
                    <div className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] bg-[#0B3D2E] text-[#D4AF37] border border-[#D4AF37]/40 font-bold">
                      video slot
                    </div>
                  )}
                  <div className="aspect-square bg-[#072A1F] flex items-center justify-center overflow-hidden">
                    {showVideo && overridden ? (
                      <video
                        src={url}
                        className="w-full h-full object-cover"
                        muted
                        playsInline
                        loop
                        autoPlay
                      />
                    ) : showVideo ? (
                      <div className="text-center text-white/45 text-xs px-3">
                        <div className="text-[#D4AF37] text-3xl mb-1">▶</div>
                        Upload an MP4 / WEBM to enable<br />the hero video.
                      </div>
                    ) : (
                      <img
                        src={url}
                        alt={slot.label}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                      />
                    )}
                  </div>
                  <div className="p-3">
                    <div className="text-[11px] tracking-[0.15em] uppercase text-[#D4AF37] truncate">{slot.key}</div>
                    <div className="text-sm text-white/85 truncate" title={slot.label}>{slot.label}</div>

                    <input
                      ref={(el) => { fileRefs.current[slot.key] = el; }}
                      type="file" accept={accept}
                      data-testid={`asset-file-${slot.key}`}
                      className="hidden"
                      onChange={(e) => onUpload(slot, e.target.files?.[0])}
                    />
                    <div className="flex gap-1.5 mt-3">
                      <button
                        type="button" disabled={isBusy}
                        onClick={() => fileRefs.current[slot.key]?.click()}
                        data-testid={`asset-replace-${slot.key}`}
                        className="flex-1 py-1.5 rounded-full text-xs bg-[#D4AF37] text-[#0B3D2E] font-semibold hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-1.5"
                      >
                        {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : isOk ? <Check className="w-3 h-3" /> : <Upload className="w-3 h-3" />}
                        {overridden ? 'Replace' : 'Upload'}
                      </button>
                      {overridden && (
                        <button
                          type="button" disabled={isBusy}
                          onClick={() => onReset(slot)}
                          data-testid={`asset-reset-${slot.key}`}
                          title="Reset to default"
                          className="px-3 py-1.5 rounded-full text-xs bg-[#124D3A] border border-[#D4AF37]/25 text-white/85 hover:text-[#D4AF37] disabled:opacity-60"
                        >
                          <RotateCcw className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </details>
      ))}
    </div>
  );
}
